import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, onUnmounted } from 'vue';
import { datepickerProps, datepickerEmits, MONTHS_SHORT, type PickerValue, type DayCell, type MonthCell } from './datepicker';
import UiDatepickerDate from './types/date/date.vue';
import UiDatepickerDateTime from './types/dateTime/dateTime.vue';
import UiDatepickerDateRangePlus from './types/dateRangePlus/dateRangePlus.vue';
import UiDatepickerMonth from './types/month/month.vue';
import { UPDATE_MODEL_VALUE } from '../../constants';
import { type TooltipBinding } from '../../directives';
import { UiIconClose } from '../../icons';

interface DatePreset { label: string; start: Date | null; end: Date | null }


const __default__ = {
    name: 'UiDatepicker',
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: datepickerProps,
  emits: datepickerEmits,
  setup(__props, { emit: emits }) {

const props = __props

const pad = (n: number): string => String(n).padStart(2, '0');

const toLocalDate = (value: Date | string): Date => {
    if (value instanceof Date) return value;

    const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(value);
    if (m) {
        const [, y, mo, d, hh, mi, ss] = m;
        return new Date(Number(y), Number(mo) - 1, Number(d), Number(hh ?? 0), Number(mi ?? 0), Number(ss ?? 0));
    }

    return new Date(value);
};




const MONTHS_LONG = [
    'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

const YEAR_MIN = 1900;
const YEAR_MAX = 2099;

const activatorRef = ref<HTMLElement | null>(null);
const popupRef = ref<HTMLElement | null>(null);

const isOpen = ref(false);
const showMonthPicker = ref(false);
const showYearPicker = ref(false);

const now = new Date();
const viewYear = ref(now.getFullYear());
const viewMonth = ref(now.getMonth());

const pendingDate = ref<Date | null>(null);
const footerInputStr = ref('');
const footerRangeStartStr = ref('');
const footerRangeEndStr = ref('');
const pendingRangeStart = ref<Date | null>(null);
const pendingRangeEnd = ref<Date | null>(null);
const hoverDate = ref<Date | null>(null);
const hoverMonth = ref<Date | null>(null);
const activePreset = ref<string | null>(null);

const parseModelValue = (value: PickerValue | null | undefined): Date | null => {
    if (!value) return null;

    if (typeof value === 'object') {
        const o = value as { year?: string; month?: string; day?: string; hours?: string; minutes?: string; seconds?: string };
        const y = parseInt(o.year ?? '0');
        if (!y) return null;
        const date = new Date(y, parseInt(o.month ?? '1') - 1, parseInt(o.day ?? '1'), parseInt(o.hours ?? '0'), parseInt(o.minutes ?? '0'), parseInt(o.seconds ?? '0'));
        return isNaN(date.getTime()) ? null : date;
    }

    const str = value as string;
    const fmt = props.format;

    const extract = (token: string, fallback = 0): number => {
        const idx = fmt.indexOf(token);
        return idx === -1 ? fallback : (parseInt(str.slice(idx, idx + token.length)) || fallback);
    };

    const y = extract('YYYY');
    if (!y) return null;

    const date = new Date(
        y,
        extract('MM', 1) - 1,
        extract('DD', 1),
        extract('HH'),
        extract('mm'),
        extract('ss'),
    );
    return isNaN(date.getTime()) ? null : date;
};

const formatDMY = (date: Date): string =>
    `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;

const buildOutputValue = (date: Date): string | PickerValue => {
    const fmt = props.format;

    if (props.returnObject) {
        const obj: Record<string, string> = {};
        if (fmt.includes('YYYY')) obj.year = String(date.getFullYear());
        if (fmt.includes('MM')) obj.month = pad(date.getMonth() + 1);
        if (fmt.includes('DD')) obj.day = pad(date.getDate());
        if (fmt.includes('HH')) obj.hours = pad(date.getHours());
        if (fmt.includes('mm')) obj.minutes = pad(date.getMinutes());
        if (fmt.includes('ss')) obj.seconds = pad(date.getSeconds());
        return obj as PickerValue;
    }

    return fmt
        .replace('YYYY', String(date.getFullYear()))
        .replace('MM', pad(date.getMonth() + 1))
        .replace('DD', pad(date.getDate()))
        .replace('HH', pad(date.getHours()))
        .replace('mm', pad(date.getMinutes()))
        .replace('ss', pad(date.getSeconds()));
};

const emptyValue = (): string | PickerValue | string[] | PickerValue[] =>
    (isRange.value || isMonthRange.value)
        ? (props.returnObject ? [{}, {}] as PickerValue[] : ['', ''])
        : (props.returnObject ? {} : '');

const startOf = (date: Date): number => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
};

const isDateDisabled = (date: Date): boolean => {
    const av = props.availableDates;
    if (!av) return false;

    const d = new Date(date);
    d.setHours(0, 0, 0, 0);

    if (av.start) {
        const s = toLocalDate(av.start);
        s.setHours(0, 0, 0, 0);
        if (d < s) return true;
    }

    if (av.end) {
        const e = toLocalDate(av.end);
        e.setHours(0, 0, 0, 0);
        if (d > e) return true;
    }

    return false;
};

const parseDMY = (str: string): Date | null => {
    const m = str.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (!m) return null;
    const [, dd, mo, yyyy] = m.map(Number);
    const date = new Date(yyyy, mo - 1, dd);
    if (
        date.getFullYear() !== yyyy ||
        date.getMonth() !== mo - 1 ||
        date.getDate() !== dd
    ) return null;
    return date;
};

const getAnchor = (): Date => {
    let anchor = new Date();
    if (props.availableDates && isDateDisabled(anchor)) {
        anchor = props.availableDates.start
            ? toLocalDate(props.availableDates.start)
            : props.availableDates.end
                ? toLocalDate(props.availableDates.end)
                : anchor;
    }
    return anchor;
};

const initFromModel = (): void => {
    if (isMonthRange.value) {
        const mv = props.modelValue;
        if (Array.isArray(mv) && mv.length >= 2) {
            const s = parseModelValue(mv[0]);
            const e = parseModelValue(mv[1]);
            pendingRangeStart.value = s;
            pendingRangeEnd.value = e;
            const anchor = s ?? e;
            if (anchor) viewYear.value = anchor.getFullYear();
        } else {
            pendingRangeStart.value = null;
            pendingRangeEnd.value = null;
            viewYear.value = new Date().getFullYear();
        }
        return;
    }

    if (isMonthPicker.value) {
        const date = parseModelValue(props.modelValue as PickerValue | null);
        pendingDate.value = date;
        if (date) viewYear.value = date.getFullYear();
        return;
    }

    if (isRangeMode.value) {
        const mv = props.modelValue;
        if (Array.isArray(mv) && mv.length >= 2) {
            const s = parseModelValue(mv[0]);
            const e = parseModelValue(mv[1]);
            pendingRangeStart.value = s;
            pendingRangeEnd.value = e;
            const anchor = s ?? e ?? getAnchor();
            viewYear.value = anchor.getFullYear();
            viewMonth.value = anchor.getMonth();
        } else {
            pendingRangeStart.value = null;
            pendingRangeEnd.value = null;
            const anchor = getAnchor();
            viewYear.value = anchor.getFullYear();
            viewMonth.value = anchor.getMonth();
        }
        return;
    }

    const date = parseModelValue(props.modelValue as PickerValue | null);

    if (date) {
        pendingDate.value = date;
        viewYear.value = date.getFullYear();
        viewMonth.value = date.getMonth();
        footerInputStr.value = formatDMY(date);
        return;
    }

    const anchor = getAnchor();
    pendingDate.value = isDateTime.value ? new Date() : null;
    viewYear.value = anchor.getFullYear();
    viewMonth.value = anchor.getMonth();
    footerInputStr.value = '';
};

const isDate = computed(() => props.type === 'date' || props.type === 'date-range');
const isRange = computed(() => props.type === 'date-range');
const isRangePlus = computed(() => props.type === 'date-range-plus');
const isRangeMode = computed(() => isRange.value || isRangePlus.value);
const isDateTime = computed(() => props.type === 'date-time');
const isMonthRange = computed(() => props.type === 'month-range');
const isMonthPicker = computed(() => props.type === 'month' || props.type === 'month-range');

const rangeEffectiveEnd = computed<Date | null>(() =>
    pendingRangeEnd.value ?? hoverDate.value,
);

const rangeDirection = computed<'--to-left' | '--to-right' | null>(() => {
    if (!isRangeMode.value || !pendingRangeStart.value) return null;
    const effEnd = rangeEffectiveEnd.value;
    if (!effEnd) return null;
    return effEnd.getTime() >= pendingRangeStart.value.getTime() ? '--to-right' : '--to-left';
});

const displayValue = computed<string>(() => {
    if (isMonthRange.value) {
        const mv = props.modelValue;
        if (!Array.isArray(mv) || mv.length < 2) return '';
        const s = parseModelValue(mv[0]);
        const e = parseModelValue(mv[1]);
        if (!s || !e) return '';
        return `${MONTHS_LONG[s.getMonth()]} ${s.getFullYear()} – ${MONTHS_LONG[e.getMonth()]} ${e.getFullYear()}`;
    }

    if (isMonthPicker.value) {
        const date = parseModelValue(props.modelValue as PickerValue | null);
        if (!date) return '';
        return `${MONTHS_LONG[date.getMonth()]} ${date.getFullYear()}`;
    }

    if (isRangeMode.value) {
        const mv = props.modelValue;
        if (!Array.isArray(mv) || mv.length < 2) return '';
        const s = parseModelValue(mv[0]);
        const e = parseModelValue(mv[1]);
        if (!s || !e) return '';
        return `${formatDMY(s)} — ${formatDMY(e)}`;
    }

    const date = parseModelValue(props.modelValue as PickerValue | null);
    if (!date) return '';

    const fmt = props.format;
    const hasDay = fmt.includes('DD');
    const hasMonth = fmt.includes('MM');
    const hasYear = fmt.includes('YYYY');
    const hasHours = fmt.includes('HH');
    const hasMins = fmt.includes('mm');
    const hasSecs = fmt.includes('ss');

    let datePart = '';
    if (hasDay && hasMonth && hasYear) datePart = formatDMY(date);
    else if (hasMonth && hasYear) datePart = `${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
    else if (hasYear) datePart = String(date.getFullYear());

    if (!hasHours) return datePart;

    let timePart = pad(date.getHours());
    if (hasMins) timePart += `:${pad(date.getMinutes())}`;
    if (hasSecs) timePart += `:${pad(date.getSeconds())}`;

    return `${datePart} ${timePart}`;
});

const timeFormat = computed<'HH:mm:ss' | 'HH:mm' | 'HH'>(() => {
    if (props.onlyHours) return 'HH';
    const fmt = props.format;
    if (fmt.includes('ss')) return 'HH:mm:ss';
    if (fmt.includes('mm')) return 'HH:mm';
    return 'HH';
});

const pendingTimeStr = computed<string>(() => {
    const d = pendingDate.value ?? new Date();
    return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
});

const footerTimeStr = computed<string>(() => {
    if (!pendingDate.value) return '';
    const h = pad(pendingDate.value.getHours());
    const m = pad(pendingDate.value.getMinutes());
    const s = pad(pendingDate.value.getSeconds());
    const fmt = timeFormat.value;
    if (fmt === 'HH:mm:ss') return `${h}:${m}:${s}`;
    if (fmt === 'HH:mm') return `${h}:${m}`;
    return h;
});

const viewMonth2 = computed(() => viewMonth.value === 11 ? 0 : viewMonth.value + 1);
const viewYear2 = computed(() => viewMonth.value === 11 ? viewYear.value + 1 : viewYear.value);
const navTitle2 = computed(() => `${MONTHS_LONG[viewMonth2.value]} ${viewYear2.value}`);

const PRESETS = computed<DatePreset[]>(() => {
    const t = new Date(); t.setHours(0, 0, 0, 0);
    const d = (n: number) => { const x = new Date(t); x.setDate(t.getDate() + n); return x; };
    const dow = t.getDay() === 0 ? 6 : t.getDay() - 1;
    const wStart = d(-dow);
    const pwStart = d(-dow - 7);
    const mStart = new Date(t.getFullYear(), t.getMonth(), 1);
    const mEnd = new Date(t.getFullYear(), t.getMonth() + 1, 0);
    const pmStart = new Date(t.getFullYear(), t.getMonth() - 1, 1);
    const pmEnd = new Date(t.getFullYear(), t.getMonth(), 0);
    return [
        { label: 'Сегодня', start: t, end: t },
        { label: 'Вчера', start: d(-1), end: d(-1) },
        { label: 'Последние 7 дней', start: d(-6), end: t },
        { label: 'За текущую неделю', start: wStart, end: d(-dow + 6) },
        { label: 'За прошлую неделю', start: pwStart, end: d(-dow - 1) },
        { label: 'За текущий месяц', start: mStart, end: mEnd },
        { label: 'За прошлый месяц', start: pmStart, end: pmEnd },
        { label: 'За текущий год', start: new Date(t.getFullYear(), 0, 1), end: new Date(t.getFullYear(), 11, 31) },
        { label: 'Произвольный период', start: null, end: null },
    ];
});

const yearRange = computed<number[]>(() => {
    const arr: number[] = [];
    for (let y = YEAR_MAX; y >= YEAR_MIN; y--) arr.push(y);
    return arr;
});

const isClearable = computed<boolean>(() =>
    !!props.clearable && !props.disabled && !props.readonly,
);

const navTitle = computed<string>(() =>
    `${MONTHS_LONG[viewMonth.value]} ${viewYear.value}`,
);

const buildCells = (y: number, mo: number): DayCell[] => {
    const firstDow = (new Date(y, mo, 1).getDay() + 6) % 7;
    const daysInMonth = new Date(y, mo + 1, 0).getDate();
    const daysInPrevMonth = new Date(y, mo, 0).getDate();
    const todayStr = `${now.getFullYear()}-${now.getMonth()}-${now.getDate()}`;

    const isSel = (date: Date): boolean =>
        !isRangeMode.value &&
        !!pendingDate.value &&
        pendingDate.value.getFullYear() === date.getFullYear() &&
        pendingDate.value.getMonth() === date.getMonth() &&
        pendingDate.value.getDate() === date.getDate();

    let rStartTs: number | null = null;
    let rEndTs: number | null = null;
    let rangeMin: number | null = null;
    let rangeMax: number | null = null;

    if (isRangeMode.value && pendingRangeStart.value) {
        rStartTs = startOf(pendingRangeStart.value);
        const effEnd = rangeEffectiveEnd.value;
        if (effEnd) {
            rEndTs = startOf(effEnd);
            rangeMin = Math.min(rStartTs, rEndTs);
            rangeMax = Math.max(rStartTs, rEndTs);
        }
    }

    const rProps = (date: Date) => {
        if (!isRangeMode.value) return { isRangeStart: false, isRangeEnd: false, isInRange: false };
        const ts = startOf(date);
        return {
            isRangeStart: rStartTs !== null && ts === rStartTs,
            isRangeEnd: rEndTs !== null && ts === rEndTs && rStartTs !== null,
            isInRange: rangeMin !== null && rangeMax !== null && ts >= rangeMin && ts <= rangeMax,
        };
    };

    const cells: DayCell[] = [];

    for (let i = firstDow - 1; i >= 0; i--) {
        const d = daysInPrevMonth - i;
        const date = new Date(y, mo - 1, d);
        cells.push({ key: `p-${d}`, day: d, date, isOtherMonth: true, isToday: false, isSelected: isSel(date), isDisabled: isDateDisabled(date), ...rProps(date) });
    }
    for (let d = 1; d <= daysInMonth; d++) {
        const date = new Date(y, mo, d);
        cells.push({ key: `c-${d}`, day: d, date, isOtherMonth: false, isToday: `${y}-${mo}-${d}` === todayStr, isSelected: isSel(date), isDisabled: isDateDisabled(date), ...rProps(date) });
    }
    let nd = 1;
    while (cells.length < 42) {
        const date = new Date(y, mo + 1, nd);
        cells.push({ key: `n-${nd}`, day: nd, date, isOtherMonth: true, isToday: false, isSelected: isSel(date), isDisabled: isDateDisabled(date), ...rProps(date) });
        nd++;
    }
    return cells;
};

const dayCells = computed<DayCell[]>(() => buildCells(viewYear.value, viewMonth.value));
const dayCells2 = computed<DayCell[]>(() => buildCells(viewYear2.value, viewMonth2.value));

const periodDisplay = computed<string>(() => {
    if (!pendingRangeStart.value) return '';
    const effEnd = rangeEffectiveEnd.value;
    const s = formatDMY(pendingRangeStart.value);
    const e = effEnd ? formatDMY(effEnd) : 'дд.мм.гггг';
    return `${s} — ${e}`;
});

const activeRangePeriodDisplay = computed<string>(() => {
    if (isMonthRange.value) {
        if (!pendingRangeStart.value) return '';
        const effEnd = pendingRangeEnd.value ?? hoverMonth.value;
        const s = `${MONTHS_LONG[pendingRangeStart.value.getMonth()]} ${pendingRangeStart.value.getFullYear()}`;
        const e = effEnd
            ? `${MONTHS_LONG[effEnd.getMonth()]} ${effEnd.getFullYear()}`
            : 'мм.гггг';
        return `${s} – ${e}`;
    }
    return periodDisplay.value;
});

const teleportBinding = computed<TooltipBinding>(() => ({
    toggle: isOpen.value,
    ref: activatorRef.value,
    placement: props.position,
    offset: 4,
    width: 'max-content',
    containInViewport: true,
}));

const handleDocumentClick = (e: MouseEvent): void => {
    const target = e.target as Node;
    if (activatorRef.value?.contains(target)) return;
    if (popupRef.value?.contains(target)) return;
    isOpen.value = false;
};

const handleDocumentKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') isOpen.value = false;
};

watch(isOpen, (open) => {
    if (open) {
        document.addEventListener('click', handleDocumentClick);
        document.addEventListener('keydown', handleDocumentKeydown);
    } else {
        document.removeEventListener('click', handleDocumentClick);
        document.removeEventListener('keydown', handleDocumentKeydown);
        showMonthPicker.value = false;
        showYearPicker.value = false;
    }
});

watch(pendingRangeStart, (val) => {
    footerRangeStartStr.value = val ? formatDMY(val) : '';
});

watch(pendingRangeEnd, (val) => {
    footerRangeEndStr.value = val ? formatDMY(val) : '';
});

onUnmounted(() => {
    document.removeEventListener('click', handleDocumentClick);
    document.removeEventListener('keydown', handleDocumentKeydown);
});

const handleActivatorClick = (): void => {
    if (props.disabled || props.readonly) return;
    if (!isOpen.value) initFromModel();
    isOpen.value = !isOpen.value;
};

const handleClear = (): void => {
    if (isRange.value || isMonthRange.value) {
        pendingRangeStart.value = null;
        pendingRangeEnd.value = null;
        hoverMonth.value = null;
    } else {
        pendingDate.value = null;
        footerInputStr.value = '';
    }
    emits(UPDATE_MODEL_VALUE, emptyValue());
    isOpen.value = false;
};

const prevMonth = (): void => {
    if (viewMonth.value === 0) { viewMonth.value = 11; viewYear.value--; }
    else viewMonth.value--;
};

const nextMonth = (): void => {
    if (viewMonth.value === 11) { viewMonth.value = 0; viewYear.value++; }
    else viewMonth.value++;
};

const prevYear = (): void => { viewYear.value--; };
const nextYear = (): void => { viewYear.value++; };

const toggleMonthPicker = (): void => {
    showMonthPicker.value = !showMonthPicker.value;
};

const selectMonth = (idx: number): void => {
    viewMonth.value = idx;
    showMonthPicker.value = false;
};

const handleDayClick = (cell: DayCell): void => {
    if (cell.isDisabled) return;

    if (isRangeMode.value) {
        handleRangeDayClick(cell.date);
        return;
    }

    if (isDateTime.value) {
        const base = pendingDate.value ?? new Date();
        const d = new Date(cell.date);
        d.setHours(base.getHours(), base.getMinutes(), base.getSeconds());
        pendingDate.value = d;
    } else {
        pendingDate.value = cell.date;
    }
    footerInputStr.value = formatDMY(cell.date);
    if (cell.isOtherMonth) {
        viewMonth.value = cell.date.getMonth();
        viewYear.value = cell.date.getFullYear();
    }
};

const selectPreset = (preset: DatePreset): void => {
    activePreset.value = preset.label;
    if (preset.start && preset.end) {
        pendingRangeStart.value = preset.start;
        pendingRangeEnd.value = preset.end;
        viewYear.value = preset.start.getFullYear();
        viewMonth.value = preset.start.getMonth();
    } else {
        pendingRangeStart.value = null;
        pendingRangeEnd.value = null;
    }
};

const presetLabels = computed<string[]>(() => PRESETS.value.map((p) => p.label));

const handleSelectPreset = (label: string): void => {
    const preset = PRESETS.value.find((p) => p.label === label);
    if (preset) selectPreset(preset);
};

const isMonthSelected = (idx: number): boolean =>
    !isMonthRange.value &&
    !!pendingDate.value &&
    pendingDate.value.getFullYear() === viewYear.value &&
    pendingDate.value.getMonth() === idx;

const monthCellTs = (idx: number): number => new Date(viewYear.value, idx, 1).getTime();

const isMonthCellRangeStart = (idx: number): boolean => {
    if (!isMonthRange.value || !pendingRangeStart.value) return false;
    const effEnd = pendingRangeEnd.value ?? hoverMonth.value;
    const startTs = new Date(pendingRangeStart.value.getFullYear(), pendingRangeStart.value.getMonth(), 1).getTime();
    const endTs = effEnd ? new Date(effEnd.getFullYear(), effEnd.getMonth(), 1).getTime() : null;
    const rangeMin = endTs !== null ? Math.min(startTs, endTs) : startTs;
    return monthCellTs(idx) === rangeMin;
};

const isMonthCellRangeEnd = (idx: number): boolean => {
    if (!isMonthRange.value || !pendingRangeStart.value) return false;
    const effEnd = pendingRangeEnd.value ?? hoverMonth.value;
    if (!effEnd) return false;
    const startTs = new Date(pendingRangeStart.value.getFullYear(), pendingRangeStart.value.getMonth(), 1).getTime();
    const endTs = new Date(effEnd.getFullYear(), effEnd.getMonth(), 1).getTime();
    const rangeMax = Math.max(startTs, endTs);
    return monthCellTs(idx) === rangeMax;
};

const isMonthCellInRange = (idx: number): boolean => {
    if (!isMonthRange.value || !pendingRangeStart.value) return false;
    const effEnd = pendingRangeEnd.value ?? hoverMonth.value;
    if (!effEnd) return false;
    const startTs = new Date(pendingRangeStart.value.getFullYear(), pendingRangeStart.value.getMonth(), 1).getTime();
    const endTs = new Date(effEnd.getFullYear(), effEnd.getMonth(), 1).getTime();
    const rangeMin = Math.min(startTs, endTs);
    const rangeMax = Math.max(startTs, endTs);
    const ts = monthCellTs(idx);
    return ts > rangeMin && ts < rangeMax;
};

const monthCells = computed<MonthCell[]>(() =>
    MONTHS_SHORT.map((label, idx) => ({
        idx,
        label,
        isSelected: isMonthSelected(idx),
        isRangeStart: isMonthCellRangeStart(idx),
        isRangeEnd: isMonthCellRangeEnd(idx),
        isInRange: isMonthCellInRange(idx),
    })),
);

const handleMonthHover = (idx: number): void => {
    if (!isMonthRange.value) return;
    if (pendingRangeStart.value && !pendingRangeEnd.value) {
        hoverMonth.value = new Date(viewYear.value, idx, 1);
    }
};

const selectMonthValue = (monthIdx: number): void => {
    if (isMonthRange.value) {
        const date = new Date(viewYear.value, monthIdx, 1);
        if (pendingRangeStart.value && pendingRangeEnd.value) {
            pendingRangeStart.value = date;
            pendingRangeEnd.value = null;
            return;
        }
        if (!pendingRangeStart.value) {
            pendingRangeStart.value = date;
            return;
        }
        const startTs = new Date(pendingRangeStart.value.getFullYear(), pendingRangeStart.value.getMonth(), 1).getTime();
        if (date.getTime() >= startTs) {
            pendingRangeEnd.value = date;
        } else {
            pendingRangeEnd.value = pendingRangeStart.value;
            pendingRangeStart.value = date;
        }
        hoverMonth.value = null;
        const rv = buildRangeOutputValue();
        if (rv) {
            emits(UPDATE_MODEL_VALUE, rv);
            isOpen.value = false;
        }
        return;
    }
    const date = new Date(viewYear.value, monthIdx, 1);
    pendingDate.value = date;
    emits(UPDATE_MODEL_VALUE, buildOutputValue(date));
    isOpen.value = false;
};

const toggleYearPicker = (): void => {
    showYearPicker.value = !showYearPicker.value;
};

const selectYear = (year: number): void => {
    viewYear.value = year;
    showYearPicker.value = false;
};

const handleRangeDayClick = (date: Date): void => {
    activePreset.value = null;
    if (pendingRangeStart.value && pendingRangeEnd.value) {
        pendingRangeStart.value = date;
        pendingRangeEnd.value = null;
        return;
    }

    if (!pendingRangeStart.value) {
        pendingRangeStart.value = date;
        return;
    }

    if (startOf(date) >= startOf(pendingRangeStart.value)) {
        pendingRangeEnd.value = date;
    } else {
        pendingRangeEnd.value = pendingRangeStart.value;
        pendingRangeStart.value = date;
    }
    hoverDate.value = null;
};

const handleDayHover = (cell: DayCell): void => {
    if (!isRangeMode.value || cell.isDisabled) return;
    if (pendingRangeStart.value && !pendingRangeEnd.value) {
        hoverDate.value = cell.date;
    }
};

const handleTimeChange = (val: string | { hours?: string; minutes?: string; seconds?: string }): void => {
    const base = pendingDate.value ? new Date(pendingDate.value) : new Date();
    const only = props.onlyHours;
    if (typeof val === 'string') {
        const [h, m, s] = val.split(':').map(Number);
        base.setHours(h || 0, only ? 0 : (m || 0), only ? 0 : (s || 0));
    } else {
        base.setHours(
            parseInt(val.hours ?? '0') || 0,
            only ? 0 : (parseInt(val.minutes ?? '0') || 0),
            only ? 0 : (parseInt(val.seconds ?? '0') || 0),
        );
    }
    pendingDate.value = new Date(base);
};

const handleFooterTimeInput = (value: string): void => {
    const digits = value.replace(/[^\d:]/g, '');
    const parts = digits.split(':').map(Number);
    if (parts.length >= 2 && digits.length >= 5) {
        const base = pendingDate.value ? new Date(pendingDate.value) : new Date();
        base.setHours(parts[0] || 0, parts[1] || 0, parts[2] || 0);
        pendingDate.value = new Date(base);
    }
};

const handleFooterTimeChange = (value: string): void => {
    const parts = value.split(':').map(Number);
    if (!parts.length || isNaN(parts[0])) return;
    const base = pendingDate.value ? new Date(pendingDate.value) : new Date();
    base.setHours(parts[0] || 0, parts[1] || 0, parts[2] || 0);
    pendingDate.value = new Date(base);
};

const buildRangeOutputValue = (): [string | PickerValue, string | PickerValue] | null => {
    if (!pendingRangeStart.value || !pendingRangeEnd.value) return null;
    return [buildOutputValue(pendingRangeStart.value), buildOutputValue(pendingRangeEnd.value)] as any;
};

const applyDateMask = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 8);
    let formatted = digits.slice(0, 2);
    if (digits.length > 2) formatted += '.' + digits.slice(2, 4);
    if (digits.length > 4) formatted += '.' + digits.slice(4, 8);
    return formatted;
};

const handleFooterInput = (value: string): void => {
    const formatted = applyDateMask(value);
    footerInputStr.value = formatted;
    if (formatted.length === 10) {
        const date = parseDMY(formatted);
        if (date) {
            pendingDate.value = date;
            viewYear.value = date.getFullYear();
            viewMonth.value = date.getMonth();
        }
    }
};

const handleFooterChange = (value: string): void => {
    const date = parseDMY(value);
    if (date) {
        pendingDate.value = date;
        viewYear.value = date.getFullYear();
        viewMonth.value = date.getMonth();
        footerInputStr.value = formatDMY(date);
    }
};

const handleFooterRangeStartInput = (value: string): void => {
    const formatted = applyDateMask(value);
    footerRangeStartStr.value = formatted;
    if (formatted.length === 10) {
        const date = parseDMY(formatted);
        if (date) {
            pendingRangeStart.value = date;
            viewYear.value = date.getFullYear();
            viewMonth.value = date.getMonth();
        }
    }
};

const handleFooterRangeStartChange = (value: string): void => {
    const date = parseDMY(value);
    if (date) {
        pendingRangeStart.value = date;
        viewYear.value = date.getFullYear();
        viewMonth.value = date.getMonth();
    } else {
        footerRangeStartStr.value = '';
        pendingRangeStart.value = null;
    }
};

const handleFooterRangeEndInput = (value: string): void => {
    const formatted = applyDateMask(value);
    footerRangeEndStr.value = formatted;
    if (formatted.length === 10) {
        const date = parseDMY(formatted);
        if (date) pendingRangeEnd.value = date;
    }
};

const handleFooterRangeEndChange = (value: string): void => {
    const date = parseDMY(value);
    if (date) {
        pendingRangeEnd.value = date;
    } else {
        footerRangeEndStr.value = '';
        pendingRangeEnd.value = null;
    }
};

const handleReset = (): void => {
    pendingDate.value = null;
    footerInputStr.value = '';
    pendingRangeStart.value = null;
    pendingRangeEnd.value = null;
    hoverDate.value = null;
    hoverMonth.value = null;
    activePreset.value = null;
    const n = new Date();
    viewYear.value = n.getFullYear();
    viewMonth.value = n.getMonth();
    emits(UPDATE_MODEL_VALUE, emptyValue());
};

const handleApply = (): void => {
    if (isRangeMode.value || isMonthRange.value) {
        const rv = buildRangeOutputValue();
        emits(UPDATE_MODEL_VALUE, rv ?? emptyValue());
    } else {
        emits(UPDATE_MODEL_VALUE, pendingDate.value ? buildOutputValue(pendingDate.value) : emptyValue());
    }
    isOpen.value = false;
};

const handleSelectGrouping = (value: string): void => {
    emits('update:groupingValue', value);
};

watch(
    () => props.modelValue,
    () => { if (!isOpen.value) initFromModel(); },
    { immediate: true },
);

return { __sfc: true,pad, toLocalDate, props, emits, MONTHS_LONG, YEAR_MIN, YEAR_MAX, activatorRef, popupRef, isOpen, showMonthPicker, showYearPicker, now, viewYear, viewMonth, pendingDate, footerInputStr, footerRangeStartStr, footerRangeEndStr, pendingRangeStart, pendingRangeEnd, hoverDate, hoverMonth, activePreset, parseModelValue, formatDMY, buildOutputValue, emptyValue, startOf, isDateDisabled, parseDMY, getAnchor, initFromModel, isDate, isRange, isRangePlus, isRangeMode, isDateTime, isMonthRange, isMonthPicker, rangeEffectiveEnd, rangeDirection, displayValue, timeFormat, pendingTimeStr, footerTimeStr, viewMonth2, viewYear2, navTitle2, PRESETS, yearRange, isClearable, navTitle, buildCells, dayCells, dayCells2, periodDisplay, activeRangePeriodDisplay, teleportBinding, handleDocumentClick, handleDocumentKeydown, handleActivatorClick, handleClear, prevMonth, nextMonth, prevYear, nextYear, toggleMonthPicker, selectMonth, handleDayClick, selectPreset, presetLabels, handleSelectPreset, isMonthSelected, monthCellTs, isMonthCellRangeStart, isMonthCellRangeEnd, isMonthCellInRange, monthCells, handleMonthHover, selectMonthValue, toggleYearPicker, selectYear, handleRangeDayClick, handleDayHover, handleTimeChange, handleFooterTimeInput, handleFooterTimeChange, buildRangeOutputValue, applyDateMask, handleFooterInput, handleFooterChange, handleFooterRangeStartInput, handleFooterRangeStartChange, handleFooterRangeEndInput, handleFooterRangeEndChange, handleReset, handleApply, handleSelectGrouping, UiDatepickerDate, UiDatepickerDateTime, UiDatepickerDateRangePlus, UiDatepickerMonth, UiIconClose }
}

})